type ISODateString = string & { readonly __brand: 'ISODateString' };

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * This function is REQUIRED to be used for the dates used in the filters, to ensure they are in the correct format and to provide type safety.
 * Safely attempts to brand a string as an ISO date.
 * Returns the cast value on success, or the thrown error on failure.
 */
export const tryToISODateString = (value: string): { value?: ISODateString; error?: Error } => {
  try {
    if (!ISO_DATE_REGEX.test(value)) {
      throw new Error(`Invalid ISO date string: "${value}". Expected format: YYYY-MM-DD`);
    }

    return { value: value as ISODateString };
  } catch (error) {
    return {
      error: error instanceof Error
        ? error
        : new Error(`Invalid ISO date string: "${value}". Expected format: YYYY-MM-DD`),
    };
  }
};

/**
 * Response shape from GET /audit-logs/meta.
 * `domains` is a map of domain name → array of action strings for that domain.
 */
export interface AuditLogMeta {
  domains: Record<string, string[]>;
  categories: string[];
}

export interface AuditLogFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  domain?: string;
  category?: string;
  dateFrom?: ISODateString;
  dateTo?: ISODateString;
  orderBy?: 'created_at' | 'domain' | 'action' | 'category' | 'user_id';
  orderDir?: 'ASC' | 'DESC';
}

/**
 * Builds the query params object for the `/audit-logs` endpoint.
 * Only includes properties that are explicitly provided — undefined and
 * falsy values are omitted so they don't appear in the request URL.
 *
 * @param filters - Optional filter, pagination, and sort options.
 * @param filters.page - 1-based page number.
 * @param filters.pageSize - Number of results per page (1–200, clamped server-side).
 * @param filters.search - Full-text search across domain, action, description, and user name.
 * @param filters.domain - Exact match filter on the log domain (e.g. `"Devices"`).
 * @param filters.category - Exact match filter on the log category (e.g. `"StatusChange"`).
 * @param filters.dateFrom - Inclusive start date in ISO format (YYYY-MM-DD). Use {@link tryToISODateString} to create.
 * @param filters.dateTo - Inclusive end date in ISO format (YYYY-MM-DD). Use {@link tryToISODateString} to create. Note: this function will add 1 day to the provided date to ensure the full end date is included, since the API treats `dateTo` as exclusive.
 * @param filters.orderBy - Column to sort by. Defaults to `created_at` server-side.
 * @param filters.orderDir - Sort direction. `"ASC"` or `"DESC"`.
 * @returns A partial `AuditLogFilters` object safe to pass directly as RTK Query `params`.
 */
export const buildAuditLogParams = (filters: AuditLogFilters): Partial<AuditLogFilters> => {
  // If dateTo is present, add 1 day to include the full day
  let dateTo: string | undefined = undefined;
  let adjustedDateTo: { value?: ISODateString; error?: Error } = {};
  if (filters.dateTo) {
    const date = new Date(filters.dateTo);
    if (!isNaN(date.getTime())) {
      date.setDate(date.getDate() + 1);
      dateTo = date.toISOString().slice(0, 10); // YYYY-MM-DD
    } else {
      dateTo = filters.dateTo;
    }
    adjustedDateTo = tryToISODateString(dateTo);
  }
  return {
    ...(filters.page !== undefined     && { page: filters.page }),
    ...(filters.pageSize !== undefined && { pageSize: filters.pageSize }),
    ...(filters.search && { search: filters.search }),
    ...(filters.domain && { domain: filters.domain }),
    ...(filters.category && { category: filters.category }),
    ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
    ...(adjustedDateTo.value && { dateTo: adjustedDateTo.value }),
    ...(filters.orderBy && { orderBy: filters.orderBy }),
    ...(filters.orderDir && { orderDir: filters.orderDir }),
  };
};